import React, { useEffect, useRef, useState } from 'react';
import { Brain, Zap } from 'lucide-react';
import mermaid from 'mermaid';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations/canvaTranslations';

const AISection = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const classificationRef = useRef(null);
  const suggestionRef = useRef(null);
  const [classificationId] = useState(`classification-diagram-${Math.random().toString(36).substr(2, 9)}`);
  const [suggestionId] = useState(`suggestion-diagram-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    // Initialize Mermaid
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      themeVariables: {
        primaryColor: '#00C4CC',
        primaryTextColor: '#fff',
        primaryBorderColor: '#00C4CC',
        lineColor: '#6C47FF',
        secondaryColor: '#6C47FF',
        tertiaryColor: '#f0f0f0',
      },
    });

    // AI Auto Classification Flow
    const classificationDiagram = `
      flowchart TD
        A[Incoming Message] -->|NLP Analysis| B[Content Extraction]
        B -->|Intent Detection| C{Message Type?}
        C -->|Technical| D[Technical Ticket]
        C -->|Billing| E[Billing Ticket]
        C -->|General| F[General Inquiry]
        D --> G[Route to Technical Team]
        E --> H[Route to Billing Team]
        F --> I[Route to General Support]
        
        style A fill:#00C4CC,stroke:#00C4CC,color:#fff
        style B fill:#6C47FF,stroke:#6C47FF,color:#fff
        style C fill:#00C4CC,stroke:#00C4CC,color:#fff
    `;

    // Smart Reply Suggestion Flow
    const suggestionDiagram = `
      flowchart TD
        A[Ticket Context] -->|Analyze| B[AI Engine]
        B -->|Generate| C[Reply Suggestions]
        C -->|Rank| D[Top 3 Suggestions]
        D -->|Display| E[Agent Interface]
        E -->|Select| F[Send Response]
        
        style A fill:#00C4CC,stroke:#00C4CC,color:#fff
        style B fill:#6C47FF,stroke:#6C47FF,color:#fff
        style C fill:#00C4CC,stroke:#00C4CC,color:#fff
        style D fill:#6C47FF,stroke:#6C47FF,color:#fff
    `;

    // Render classification diagram using mermaid.render (async)
    const renderClassificationDiagram = async () => {
      if (classificationRef.current) {
        try {
          classificationRef.current.innerHTML = '';
          const { svg } = await mermaid.render(classificationId, classificationDiagram);
          if (classificationRef.current) {
            classificationRef.current.innerHTML = svg;
          }
        } catch (error) {
          console.error('Mermaid rendering error:', error);
          if (classificationRef.current) {
            classificationRef.current.innerHTML = '<div class="text-red-500 p-4">Diagram rendering failed. Please refresh the page.</div>';
          }
        }
      }
    };

    // Render suggestion diagram using mermaid.render (async)
    const renderSuggestionDiagram = async () => {
      if (suggestionRef.current) {
        try {
          suggestionRef.current.innerHTML = '';
          const { svg } = await mermaid.render(suggestionId, suggestionDiagram);
          if (suggestionRef.current) {
            suggestionRef.current.innerHTML = svg;
          }
        } catch (error) {
          console.error('Mermaid rendering error:', error);
          if (suggestionRef.current) {
            suggestionRef.current.innerHTML = '<div class="text-red-500 p-4">Diagram rendering failed. Please refresh the page.</div>';
          }
        }
      }
    };

    renderClassificationDiagram();
    renderSuggestionDiagram();
  }, [classificationId, suggestionId]);

  return (
    <section
      id="ai"
      className="min-h-screen py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-gradient-to-br from-[#00C4CC] to-[#6C47FF] rounded-2xl">
              <Brain className="w-12 h-12 text-white" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.ai.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00C4CC] to-[#6C47FF] mx-auto rounded-full"></div>
          <p className="text-xl text-gray-600 mt-6 max-w-3xl mx-auto">
            {t.ai.description}
          </p>
        </div>

        {/* Two Column Layout for AI Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* AI Auto Classification */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 shadow-xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-[#00C4CC] to-[#00C4CC] rounded-lg">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {t.ai.autoClassification.title}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              {t.ai.autoClassification.description}
            </p>
            <div
              ref={classificationRef}
              className="mermaid flex items-center justify-center min-h-[300px] bg-white rounded-xl p-4"
            >
              {/* Diagram will be rendered here by Mermaid */}
            </div>
          </div>

          {/* Smart Reply Suggestions */}
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 shadow-xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-[#6C47FF] to-[#6C47FF] rounded-lg">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {t.ai.smartReply.title}
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              {t.ai.smartReply.description}
            </p>
            <div
              ref={suggestionRef}
              className="mermaid flex items-center justify-center min-h-[400px] bg-white rounded-xl p-4"
            >
              {/* Diagram will be rendered here by Mermaid */}
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-gradient-to-br from-[#00C4CC]/10 to-transparent rounded-xl">
            <div className="text-3xl font-bold text-[#00C4CC] mb-2">80%</div>
            <div className="text-gray-600">{t.ai.fasterResponse}</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-[#6C47FF]/10 to-transparent rounded-xl">
            <div className="text-3xl font-bold text-[#6C47FF] mb-2">95%</div>
            <div className="text-gray-600">{t.ai.classificationAccuracy}</div>
          </div>
          <div className="text-center p-6 bg-gradient-to-br from-[#00C4CC]/10 to-transparent rounded-xl">
            <div className="text-3xl font-bold text-[#00C4CC] mb-2">60%</div>
            <div className="text-gray-600">{t.ai.reducedWorkload}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AISection;

